import { MAX_PUSH_BATCH, backoffDelayMs } from '@pms/shared';

import { currentToken } from '../auth/session.js';
import { useAuthStore } from '../auth/store.js';
import { runPhotoQueue } from '../photos/uploader.js';
import { AuthRequiredError, RequestError, pushBatch } from './api.js';
import { getDeviceId, requestPersistentStorage } from './db.js';
import {
  OUTBOX_CHANGED_EVENT,
  applyPushResult,
  claimBatch,
  countUnsent,
  pruneSynced,
  recoverStuckSending,
  rejectBatch,
  releaseBatch,
} from './outbox.js';
import { pullAll } from './pull.js';
import { useSyncStore } from './store.js';

/**
 * Фоновый обмен с сервером.
 *
 * Отправка запускается по четырём поводам: появилась сеть, вкладка снова в фокусе,
 * сработал таймер, оператор нажал кнопку. Ни один из них не блокирует интерфейс.
 *
 * При обрыве связи запись остаётся в очереди, счётчик попыток растёт,
 * задержка удваивается до пяти минут. При отказе сервера по существу запись
 * помечается и показывается человеку — молча не исчезает ничего.
 */

const IDLE_PUSH_INTERVAL_MS = 60_000;
const PULL_INTERVAL_MS = 5 * 60_000;

let started = false;
let pushing = false;
let pulling = false;
let photosRunning = false;
let attempt = 0;
let retryTimer: ReturnType<typeof setTimeout> | null = null;
let pushInterval: ReturnType<typeof setInterval> | null = null;
let pullInterval: ReturnType<typeof setInterval> | null = null;

function clearRetry(): void {
  if (retryTimer !== null) {
    clearTimeout(retryTimer);
    retryTimer = null;
  }
}

function scheduleRetry(): void {
  clearRetry();
  const delay = backoffDelayMs(attempt);
  const store = useSyncStore.getState();
  store.setAttempt(attempt, new Date(Date.now() + delay).toISOString());
  retryTimer = setTimeout(() => {
    retryTimer = null;
    void runPush();
  }, delay);
}

/**
 * Один проход отправки: забираем пачками по 50, пока очередь не опустеет
 * или пока не упрёмся в сбой.
 */
export async function runPush(): Promise<void> {
  if (pushing) return;
  pushing = true;
  const store = useSyncStore.getState();

  try {
    for (;;) {
      const batch = await claimBatch(MAX_PUSH_BATCH);
      if (batch.length === 0) {
        attempt = 0;
        clearRetry();
        store.setAttempt(0, null);
        store.setPhase(store.online ? 'idle' : 'offline');
        // Очередь событий пуста — можно заняться фотографиями
        void runPhotos();
        return;
      }

      // Оператор мог войти без сети — тогда токена ещё нет. Не бьёмся в сервер
      // впустую и не выкидываем человека на экран входа посреди смены:
      // очередь ждёт, а в интерфейсе появляется просьба подтвердить PIN.
      if ((await currentToken()) === null) {
        await releaseBatch(
          batch.map((record) => record.id),
          'Нет подтверждения входа',
        );
        store.setError('Появилась сеть. Введите PIN, чтобы отправить накопленное');
        store.setPhase('unauthorized');
        return;
      }

      store.setPhase('sending');
      const ids = batch.map((record) => record.id);

      try {
        const deviceId = await getDeviceId();
        const response = await pushBatch(
          batch.map((record) => record.event),
          deviceId,
        );
        await applyPushResult(response.accepted, response.rejected);
        attempt = 0;
        store.markPushed();
      } catch (error) {
        // Сессия кончилась. Очередь не трогаем: после входа всё уйдёт как было.
        if (error instanceof AuthRequiredError) {
          await releaseBatch(ids, error.message);
          await useAuthStore.getState().markTokenExpired();
          store.setError('Сессия истекла — войдите заново, отчёты останутся в очереди');
          store.setPhase('unauthorized');
          return;
        }
        if (error instanceof RequestError) {
          // Сервер отказал по существу. Повтор не поможет — помечаем пачку
          // и останавливаем проход, чтобы не пометить отказом всю очередь.
          await rejectBatch(ids, error.message);
          store.setError(error.message);
          store.setPhase('error');
          return;
        }
        const message = error instanceof Error ? error.message : String(error);
        await releaseBatch(ids, message);
        attempt += 1;
        store.setError(message);
        store.setPhase(store.online ? 'waiting' : 'offline');
        scheduleRetry();
        return;
      }
    }
  } finally {
    pushing = false;
  }
}

/**
 * Фотографии — низкий приоритет. Пока в очереди есть неотправленные события,
 * фото не занимают канал: цифры отчёта должны уйти первыми.
 */
export async function runPhotos(): Promise<void> {
  if (photosRunning) return;
  if (!useSyncStore.getState().online) return;
  if ((await currentToken()) === null) return;
  if ((await countUnsent()) > 0) return;

  photosRunning = true;
  try {
    await runPhotoQueue();
  } finally {
    photosRunning = false;
  }
}

export async function runPull(): Promise<void> {
  if (pulling) return;
  // Без токена справочники не запрашиваем: 401 выкинул бы оператора,
  // вошедшего без сети, на экран входа посреди смены
  if ((await currentToken()) === null) {
    useSyncStore.getState().setPhase('unauthorized');
    return;
  }
  pulling = true;
  const store = useSyncStore.getState();
  try {
    store.setPhase('pulling');
    await pullAll();
    store.markPulled();
    store.setError(null);
  } catch (error) {
    if (error instanceof AuthRequiredError) {
      await useAuthStore.getState().markTokenExpired();
      store.setError('Сессия истекла — войдите заново');
      store.setPhase('unauthorized');
      return;
    }
    const message = error instanceof Error ? error.message : String(error);
    store.setError(message);
  } finally {
    pulling = false;
    const state = useSyncStore.getState();
    if (state.phase === 'pulling') {
      state.setPhase(state.online ? 'idle' : 'offline');
    }
  }
}

/** Ручной запуск: кнопка «Отправить сейчас» на экране очереди. */
export async function syncNow(): Promise<void> {
  attempt = 0;
  clearRetry();
  await runPush();
  await runPull();
  await runPhotos();
}

function handleOnline(): void {
  useSyncStore.getState().setOnline(true);
  attempt = 0;
  clearRetry();
  void runPush();
  void runPull();
}

function handleOffline(): void {
  const store = useSyncStore.getState();
  store.setOnline(false);
  store.setPhase('offline');
}

function handleVisibility(): void {
  if (document.visibilityState === 'visible') {
    void runPush();
  }
}

/**
 * В очереди появилась запись — пробуем отправить сразу, не дожидаясь таймера.
 * Небольшая задержка склеивает несколько действий подряд в одну отправку.
 */
let enqueueTimer: ReturnType<typeof setTimeout> | null = null;

function handleOutboxChanged(): void {
  if (enqueueTimer !== null) return;
  enqueueTimer = setTimeout(() => {
    enqueueTimer = null;
    if (useSyncStore.getState().online) void runPush();
  }, 300);
}

/**
 * Предупреждение при закрытии приложения с непустой очередью.
 * Браузер покажет своё стандартное окно — текст задать нельзя,
 * но сам факт вопроса удерживает человека от закрытия вкладки с неотправленным.
 */
function handleBeforeUnload(event: BeforeUnloadEvent): void {
  if (pendingSnapshot > 0) {
    event.preventDefault();
    event.returnValue = '';
  }
}

let pendingSnapshot = 0;
let pendingWatcher: ReturnType<typeof setInterval> | null = null;

export async function startSyncEngine(): Promise<void> {
  if (started) return;
  started = true;

  const store = useSyncStore.getState();
  store.setStoragePersisted(await requestPersistentStorage());

  // Записи, застрявшие в «отправляется» после перезагрузки, возвращаем в очередь
  await recoverStuckSending();
  await pruneSynced();
  pendingSnapshot = await countUnsent();

  window.addEventListener('online', handleOnline);
  window.addEventListener('offline', handleOffline);
  window.addEventListener('beforeunload', handleBeforeUnload);
  window.addEventListener(OUTBOX_CHANGED_EVENT, handleOutboxChanged);
  document.addEventListener('visibilitychange', handleVisibility);

  pushInterval = setInterval(() => void runPush(), IDLE_PUSH_INTERVAL_MS);
  pullInterval = setInterval(() => void runPull(), PULL_INTERVAL_MS);
  pendingWatcher = setInterval(() => {
    void countUnsent().then((count) => {
      pendingSnapshot = count;
    });
  }, 2_000);

  if (navigator.onLine) {
    void runPush();
    void runPull();
  } else {
    store.setPhase('offline');
  }
}

export function stopSyncEngine(): void {
  if (!started) return;
  started = false;

  window.removeEventListener('online', handleOnline);
  window.removeEventListener('offline', handleOffline);
  window.removeEventListener('beforeunload', handleBeforeUnload);
  window.removeEventListener(OUTBOX_CHANGED_EVENT, handleOutboxChanged);
  document.removeEventListener('visibilitychange', handleVisibility);

  clearRetry();
  if (enqueueTimer !== null) {
    clearTimeout(enqueueTimer);
    enqueueTimer = null;
  }
  if (pushInterval !== null) clearInterval(pushInterval);
  if (pullInterval !== null) clearInterval(pullInterval);
  if (pendingWatcher !== null) clearInterval(pendingWatcher);
  pushInterval = null;
  pullInterval = null;
  pendingWatcher = null;
}

/** Только для тестов: сбросить внутреннее состояние воркера. */
export function resetEngineStateForTests(): void {
  attempt = 0;
  pushing = false;
  pulling = false;
  photosRunning = false;
  clearRetry();
  if (enqueueTimer !== null) {
    clearTimeout(enqueueTimer);
    enqueueTimer = null;
  }
}

export function currentAttempt(): number {
  return attempt;
}
